import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EQMenuButton } from "./EQMenuButton";
import { EQWidget } from "./Modals";
import { useAppSelector, useEQDispatch, useEQSelector, useModal } from "src/Hooks";
import { EQModalStates, EQStateType, EQStates, PointType, ReturnEQViewStates, ReturnViewMode } from "./";
import { EQueueType, WidgetType } from "src/Types/ReDoc/EQueue";
import ReturnWidget from "src/Views/EQueue/Widgets/ReturnWidget/ReturnWidget";
import { getLearningMode } from "src/Slicers/panelSlicer";
import { autoUpdate, returnAutoUpdate, shouldExitAfterAction } from "./eQueueUtils";
import { useReturnTimers } from "./Hooks/useReturnTimers";
import { useEQActions } from "./Hooks/useEQActions";
import { useInactivityTimer } from "./Hooks/useInactivityTimer";
import { useCalledActivityDetector } from "./Hooks/useCalledActivityDetector";
import { EQueueModals } from "./EQueueModals";
import "./eQueue.scss";

interface IEnterEQProps {
	isExpanded?: boolean;
	isNeedLogout?: boolean;
	type: EQueueType;
}

export const EQueue: React.FC<IEnterEQProps> = ({ isExpanded, type, isNeedLogout = false }) => {

	const inLearningMode = useAppSelector( getLearningMode );
	const [ showWidget, setShowWidget ] = useState<boolean>(false);
	const [ widgetPosition, setWidgetPosition ] = useState<PointType|null>(null);
	const { showError } = useModal();

	const {
		dispatch, dispatchStd, getConfig, getQueueState, getStatus, start, call, getQuery, delay, redirect,
		beginServing, redirectWindow, getWindows, recall, endPause, beginPause, callById, drop,
		getDenyReasons, reason, endServing, exit
	} = useEQDispatch( type );

	const queueUI = {
		status: useEQSelector<"idle" | "pending" | "succeeded" | "failed">( type, "loading" ),
		state: useEQSelector<WidgetType>( type, "widgetState") || 0,
		modalState: useEQSelector<EQModalStates>( type, "modalState" ),
		queueCount: useEQSelector<number>( type, "allCount" ) || 0,
		statusData: useEQSelector<{ ticket?: { id?: string; idQ?: string } }>( type, "status" )
	};
	const idQ = queueUI.statusData?.ticket?.idQ || queueUI.statusData?.ticket?.id || "";

	const returnUI = {
		viewMode: useEQSelector<ReturnViewMode>( type, "viewMode" ),
		config: useEQSelector<{ pultId: string }>( type, "config" ),
		windows: useEQSelector<Array<{ id: string; text: string }>>( type, "windows" ) || [],
		windowsStatus: useEQSelector<"idle" | "loading" | "loaded">( type, "windowsStatus" ) || "idle",
		ticket: useEQSelector<{ number?: string; description?: string; inviteTime?: string } | undefined>( type, "ticket" ),
		pauseStartTime: useEQSelector<string | undefined>( type, "pauseStartTime" ),
		status: useEQSelector<{ message?: string } | undefined>( type, "status" )
	};

	const isReturnQueue = type === EQueueType.Return;

	const { pauseElapsed, callElapsed } = useReturnTimers({
		isReturnQueue,
		viewMode: returnUI.viewMode,
		pauseStartTime: returnUI.pauseStartTime,
		ticket: returnUI.ticket
	});

	useInactivityTimer({
		isActive: isReturnQueue && returnUI.viewMode !== "inactive" && returnUI.viewMode !== "pause",
		onExpire: () => exit(idQ)
	});

	useCalledActivityDetector({
		isActive: isReturnQueue && returnUI.viewMode === "called",
		onConfirm: () => beginServing()
	});

	const { onActionHandler, onCloseModal, setPostCompleteAction } = useEQActions({
		type, isReturnQueue, idQ,
		pultId: returnUI.config?.pultId ?? "",
		queueCount: queueUI.queueCount,
		showError, setShowWidget,
		dispatch, getQuery, call, callById, beginPause, endPause, drop,
		recall, beginServing, delay, redirect, redirectWindow,
		getDenyReasons, reason, endServing, exit, start, getWindows
	});

	/** При изменении режима обучения скрываем виджет */
	useEffect( () => {
		setShowWidget( prev => inLearningMode ? false : prev );
	}, [ inLearningMode ] );

	/** Принудительный выход при isNeedLogout */
	useEffect( () => {
		if ( !isNeedLogout ) return;
		if ( type === EQueueType.Return ) { exit(idQ); return; }
		switch ( queueUI.state ) {
			case WidgetType.None: return;
			case WidgetType.Waiting:
				exit(idQ);
				break;
			case WidgetType.Calling:
				Promise.resolve( drop(idQ || "") ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			case WidgetType.Working:
				Promise.resolve( endServing(idQ || "") ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			case WidgetType.OnPause:
				Promise.resolve( endPause() ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			default:
				console.warn( `Неизвестное состояние ЭО - ${WidgetType[queueUI.state]} (${queueUI.state}).` );
		}
	// eslint-disable-next-line
	}, [ isNeedLogout ] );

	/** Первичная и периодическая синхронизация с backend */
	useEffect( () => {
		getConfig();
		if ( isReturnQueue ) getStatus();
		getQueueState();
		const timerID = setInterval( getQueueState, isReturnQueue ? returnAutoUpdate : autoUpdate );
		return () => clearInterval(timerID);
	}, [] );

	/** При смене состояния разворачиваем виджет (или сворачиваем для не-return) */
	useEffect( () => {
		if ( type === EQueueType.Return ) {
			if ( queueUI.state !== 0 && !showWidget ) setShowWidget(true);
			return;
		}
		if ( queueUI.state !== 0 && !showWidget ) setShowWidget(true);
		else if ( queueUI.state === 0 ) setShowWidget(false);
	}, [ queueUI.state ] );

	/** Затемнение контента при открытом виджете или модалке */
	useEffect( () => {
		dispatchStd( { type: "waiting/setBlockContent", payload: ( showWidget || queueUI.modalState !== 0 ) } );
	}, [ showWidget, queueUI.modalState ] );

	const onLoginHandler = ( isSuccess: boolean ) => {
		if ( isSuccess ) {
			onCloseModal();
			if ( !isExpanded ) setWidgetPosition({ left: 104, top: 700 });
			setShowWidget(true);
		}
	};

	const onOpenWidgetHandler = ( position: PointType ) => {
		if ( queueUI.state !== 0 ) {
			setWidgetPosition( isExpanded ? null : { ...position, left: 104 } );
			setShowWidget( !showWidget );
		} else {
			dispatch( "openLoginForm" );
		}
	};

	const onCloseWidgetHandler = () => { if ( !isExpanded || isReturnQueue ) setShowWidget(false); };

	const renderInMenu = () => {
		const currentState = ( EQStates[queueUI.state] || EQStates[WidgetType.Unknown] ) as EQStateType;
		const returnState = isReturnQueue
			? ( ReturnEQViewStates[returnUI.viewMode || "inactive"] || ReturnEQViewStates.inactive )
			: currentState;

		const menuBtn = (
			<EQMenuButton
				isActive={!inLearningMode}
				color={isReturnQueue ? (returnState.borderTopColor || returnState.color) : currentState.color}
				isWait={queueUI.status === "pending"}
				isExpanded={isExpanded}
				title={isReturnQueue ? (returnState.text || "Очередь") : "Очередь"}
				iconClass='icon-electronic_queue'
				onClick={onOpenWidgetHandler}
			/>
		);

		if ( !showWidget ) return menuBtn;

		if ( isReturnQueue ) {
			return createPortal(
				<ReturnWidget
					state={returnState}
					queueCount={queueUI.queueCount}
					windows={returnUI.windows}
					windowsStatus={returnUI.windowsStatus}
					ticketNumber={returnUI.viewMode === "pause" ? pauseElapsed : returnUI.ticket?.number}
					restTime={returnUI.viewMode === "pause" ? pauseElapsed : returnUI.viewMode === "called" ? callElapsed : undefined}
					ticketDescription={returnUI.viewMode === "pause" ? "" : returnUI.ticket?.description}
					actions={returnState.actions}
					onClose={onCloseWidgetHandler}
					onAction={onActionHandler}
					comment={returnUI.viewMode === "called" ? returnUI.status?.message : undefined}
				/>,
				document.body
			);
		}

		const widget = <EQWidget type={type} position={widgetPosition} onClose={onCloseWidgetHandler} state={currentState} onAction={onActionHandler} />;
		if ( !!isExpanded ) return <li className="menu_item">{widget}</li>;
		return createPortal( widget, document.body );
	};

	return (
		<>
			<div onMouseDown={(e) => e.stopPropagation()}>
				<EQueueModals
					modalState={queueUI.modalState}
					type={type}
					onAction={onActionHandler}
					onClose={onCloseModal}
					onLogin={onLoginHandler}
					setShowWidget={setShowWidget}
					showError={showError}
					setPostCompleteAction={setPostCompleteAction}
				/>
			</div>
			{ renderInMenu() }
		</>
	);
};
